import { useEffect, useState } from 'react';
import { useRequireAdminAuth } from '../../hooks/useAdminAuth';
import AdminLayout from '../../components/admin/AdminLayout';
import { SpecialtyList, type SpecialtyListItem } from '../../components/admin/SpecialtyList';
import { PromptEditor } from '../../components/admin/PromptEditor';
import { PromptTester } from '../../components/admin/PromptTester';
import { PromptHistorySheet } from '../../components/admin/PromptHistorySheet';
import { DataScopePanel } from '../../components/admin/DataScopePanel';
import { NewSpecialtyModal } from '../../components/admin/NewSpecialtyModal';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  listPrompts, getPrompt, savePrompt, getPromptHistory, revertPrompt,
  listScopeSections,
  type PromptDetail, type PromptVersion, type ScopeSectionMeta,
} from '../../lib/prompts-api';
import { getAccessCodeSettings, type ClinicalSettingOption } from '../../lib/admin-api';

export default function SpecialtyPromptsView() {
  const { isLoading: authLoading, isAuthenticated } = useRequireAdminAuth();

  const [items, setItems] = useState<SpecialtyListItem[]>([]);
  const [displayNames, setDisplayNames] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string>('');
  const [detail, setDetail] = useState<PromptDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<PromptVersion[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sections, setSections] = useState<ScopeSectionMeta[]>([]);
  const [savedScope, setSavedScope] = useState<string[]>([]);
  const [draftScope, setDraftScope] = useState<string[]>([]);
  const [wantsNullScope, setWantsNullScope] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const [{ settings: catalog }, { settings: list }, { sections: catalogSections }] = await Promise.all([
          getAccessCodeSettings(),
          listPrompts(),
          listScopeSections(),
        ]);
        setSections(catalogSections);
        const groupById: Record<string, SpecialtyListItem['group']> = {};
        const nameById: Record<string, string> = {};
        catalog.forEach((c: ClinicalSettingOption) => {
          groupById[c.id] = c.group;
          nameById[c.id] = c.displayName;
        });
        setDisplayNames(nameById);
        setItems(list.map(s => ({
          id: s.id,
          displayName: s.displayName,
          group: groupById[s.id] ?? 'validated',
          hasOverride: !!s.activeVersion,
        })));
        if (list.length > 0) setSelected(list[0].id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load prompts');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!selected) return;
    setMode('view');
    setDraft('');
    setDetail(null);
    let cancelled = false;
    getPrompt(selected)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setDraft(d.body);
        setSavedScope(d.dataScope);
        setDraftScope(d.dataScope);
      })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load prompt'); });
    return () => { cancelled = true; };
  }, [selected]);

  const handleEdit = () => {
    if (!detail) return;
    setDraft(detail.body);
    setDraftScope(savedScope);
    setWantsNullScope(false);
    setMode('edit');
  };

  const handleCancel = () => {
    if (!detail) return;
    setDraft(detail.body);
    setDraftScope(savedScope);
    setWantsNullScope(false);
    setMode('view');
  };

  const handleResetScope = () => {
    setWantsNullScope(true);
  };

  const handleSave = async () => {
    if (!detail) return;
    if (draft.trim().length === 0) {
      setError('Prompt body cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const scopeToSend = wantsNullScope ? null : draftScope;
      const { version } = await savePrompt(detail.setting, draft, scopeToSend);
      const newScope = scopeToSend ?? [];
      setDetail({
        ...detail,
        body: draft,
        source: 'db',
        version: { id: version.id, createdAt: version.createdAt, createdBy: version.createdBy },
        dataScope: newScope,
        dataScopeSource: 'db',
      });
      setSavedScope(newScope);
      setDraftScope(newScope);
      setWantsNullScope(false);
      setItems(prev => prev.map(it =>
        it.id === detail.setting ? { ...it, hasOverride: true } : it
      ));
      setMode('view');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleShowHistory = async () => {
    if (!detail) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { versions } = await getPromptHistory(detail.setting);
      setHistory(versions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRevert = async (versionId: number) => {
    if (!detail) return;
    if (!confirm('Revert to this version? A new active row will be created from this body.')) return;
    try {
      await revertPrompt(detail.setting, versionId);
      const fresh = await getPrompt(detail.setting);
      setDetail(fresh);
      setDraft(fresh.body);
      setSavedScope(fresh.dataScope);
      setDraftScope(fresh.dataScope);
      const { versions } = await getPromptHistory(detail.setting);
      setHistory(versions);
      setMode('view');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revert failed');
    }
  };

  const scopeChanged = JSON.stringify([...draftScope].sort()) !== JSON.stringify([...savedScope].sort());
  const bodyChanged = detail !== null && draft !== detail.body;
  const dirty = mode === 'edit' && (bodyChanged || scopeChanged);
  const dirtyId = dirty && detail ? detail.setting : null;

  if (authLoading) {
    return (
      <AdminLayout title="Specialty Prompts">
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      </AdminLayout>
    );
  }
  if (!isAuthenticated) return null;

  return (
    <AdminLayout title="Specialty Prompts">
      {error && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
          {error}
        </div>
      )}
      <Card className="overflow-hidden p-0">
        <div className="grid grid-cols-12 min-h-[600px]">
          <div className="col-span-3 border-r border-gray-200 bg-gray-50 flex flex-col">
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>
              ) : (
                <SpecialtyList
                  items={items}
                  selected={selected}
                  onSelect={(id) => {
                    if (dirty) {
                      if (!confirm('Discard unsaved changes?')) return;
                    }
                    setSelected(id);
                  }}
                  dirtyId={dirtyId}
                />
              )}
            </div>
            <div className="p-3 border-t border-gray-200">
              <Button
                onClick={() => setCreateOpen(true)}
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                + New specialty
              </Button>
            </div>
          </div>
          <div className="col-span-3">
            {detail && (
              <>
                {wantsNullScope && mode === 'edit' && (
                  <div className="px-3 pt-2 pb-1">
                    <p className="text-[10px] italic text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      Will reset to the default scope on save.
                    </p>
                  </div>
                )}
                <DataScopePanel
                  sections={sections}
                  selected={mode === 'edit' ? draftScope : savedScope}
                  baseline={savedScope}
                  mode={mode}
                  onToggle={(id) => {
                    setWantsNullScope(false);
                    setDraftScope(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                  }}
                  canResetToDefault={mode === 'edit' && detail.dataScopeSource === 'db'}
                  onResetToDefault={handleResetScope}
                />
              </>
            )}
          </div>
          <div className="col-span-4">
            {detail ? (
              <PromptEditor
                detail={{ ...detail, displayName: displayNames[detail.setting] ?? detail.setting }}
                mode={mode}
                draft={draft}
                onDraftChange={setDraft}
                onEdit={handleEdit}
                onCancel={handleCancel}
                onSave={handleSave}
                onShowHistory={handleShowHistory}
                saving={saving}
                dirty={dirty}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                {loading ? 'Loading…' : 'Pick a specialty to view its prompt.'}
              </div>
            )}
          </div>
          <div className="col-span-2">
            {detail && (
              <PromptTester
                setting={detail.setting}
                draftBody={mode === 'edit' && bodyChanged ? draft : null}
              />
            )}
          </div>
        </div>
      </Card>
      <PromptHistorySheet
        open={historyOpen}
        versions={history}
        setting={detail?.setting ?? ''}
        onClose={() => setHistoryOpen(false)}
        onRevert={handleRevert}
      />
      {historyLoading && (
        <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-md shadow px-3 py-2 text-xs">
          Loading history…
        </div>
      )}
      <NewSpecialtyModal
        open={createOpen}
        sections={sections}
        onClose={() => setCreateOpen(false)}
        onCreated={async (newSlug) => {
          setCreateOpen(false);
          try {
            const [{ settings: catalog }, { settings: list }] = await Promise.all([
              getAccessCodeSettings(),
              listPrompts(),
            ]);
            const groupById: Record<string, SpecialtyListItem['group']> = {};
            const nameById: Record<string, string> = {};
            catalog.forEach((c: ClinicalSettingOption) => {
              groupById[c.id] = c.group;
              nameById[c.id] = c.displayName;
            });
            setDisplayNames(nameById);
            setItems(list.map(s => ({
              id: s.id,
              displayName: s.displayName,
              group: groupById[s.id] ?? 'validated',
              hasOverride: !!s.activeVersion,
            })));
            setSelected(newSlug);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to refresh sidebar after create');
          }
        }}
      />
    </AdminLayout>
  );
}
